// @ts-nocheck
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { Clickable, GridItem, Typography } from "@insite/mobius/index";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { linkStyles } from "@store/Themes/common/mixins/link";
import React, { FC, useState } from "react";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ProductPrice, { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { productCardProps } from "@store/Themes/common/props/productCard";
import ShoppingCartLine from "@store/Library/Components/Toolkit/Icons/ShoppingCart/ShoppingCartLine";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import translate from "@insite/client-framework/Translate";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { attributesProps } from "@store/Themes/common/props/attributes";
import { GridItemProps } from "@insite/mobius/GridItem";
import getAttributeTypeValue from "@store/Framework/Common/Utilities/getAttributeTypeValue";
import constants from "@store/constants";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { useGetRelatedProductInfoFromList } from "@store/Framework/Store/Store/Components/ProductInfoList/ProductInfoListHooks";
import AddToCartOptionsAndButton, {
    AddToCartOptionsAndButtonStyles,
} from "@store/Library/Components/Toolkit/AddToCartOptionsAndButton";

export interface ProductFastenersCardProps {
    product: ProductModel;
    tableId: string;
    showLength: boolean;
    showHeadDiameter: boolean;
    showThread: boolean;
    showPoint: boolean;
    showHead: boolean;
    showFinish: boolean;
}

export interface ProductFastenersCardStyles {
    wrapper?: InjectableCss;
    itemNumber?: TypographyProps;
    itemNumberLink?: LinkPresentationProps;
    productInfoWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    actionsInnerWrapper?: InjectableCss;
    addToCartOptionsAndButton?: AddToCartOptionsAndButtonStyles;
    addToListLinkStyles?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    viewDetailsWrapper?: InjectableCss;
    attributesWrapper?: InjectableCss;
    detailsClickable?: ClickablePresentationProps;
    gridContainer?: GridContainerProps;
    attributeTypeGridItem?: GridItemProps;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    productPrice?: ProductPriceStyles;
}

export const productFastenersCardStyles: ProductFastenersCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
            width: 100%;
            padding: ${pxToRem(12)};
        `,
    },
    itemNumber: {
        css: css`
            color: ${getColor("primary")};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    itemNumberLink: {
        css: css`
            ${linkStyles("text.link", "text.linkHover", false, true)}
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    productInfoWrapper: {
        css: css`
            display: flex;
            width: 40%;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 60%;
            gap: ${pxToRem(10)};

            ${breakpoint(400)} {
                flex-direction: row;
            }
        `,
    },
    actionsInnerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: ${pxToRem(10)};
        `,
    },
    addToCartOptionsAndButton: {
        ...productCardProps.addToCartOptionsAndButton,
        flexWrapper: {
            extendedStyles: {
                content: {
                    css: css`
                        flex-wrap: nowrap;
                        gap: 0;
                    `,
                },
            },
        },
        addToCartButton: {
            showButtonIconOnly: true,
            extendedStyles: {
                button: {
                    icon: {
                        position: "right",
                        src: ShoppingCartLine,
                    },
                    css: css`
                        width: 50px;
                        min-width: 50px;
                    `,
                },
            },
        },
        quantityOrdered: {
            ...productCardProps.addToCartOptionsAndButton?.quantityOrdered,
            labelOverride: "",
        },
    },
    addToListLinkStyles: productCardProps.addToListLink,
    addToListWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin-top: ${pxToRem(10)};
            width: 50%;
        `,
    },
    viewDetailsWrapper: {
        css: css`
            width: 50%;
        `,
    },
    attributesWrapper: {
        css: css`
            width: 100%;
        `,
    },
    detailsClickable: {
        css: css`
            ${linkStyles("text.accent", "text", true, true)}
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    gridContainer: {
        ...attributesProps.container,
        css: css`
            ${attributesProps.container?.css}
            font-size: ${x => x.theme.design.typography.size.smaller};
            margin-bottom: ${pxToRem(10)};
        `,
    },
    attributeTypeGridItem: attributesProps.item,
    attributeTypeText: attributesProps.label,
    attributeValueText: {
        ...attributesProps.value,
        color: "text",
    },
    productPrice: {
        wrapper: {
            css: css`
                align-items: flex-end;
            `,
        },
    },
};

const styles = productFastenersCardStyles;

type AttributeGridItemProps = {
    label: string;
    attribute: string;
    attributeTypes: AttributeTypeModel[] | undefined;
};

const AttributeGridItem: FC<AttributeGridItemProps> = ({ label, attribute, attributeTypes }) => {
    return (
        <GridItem {...styles.attributeTypeGridItem}>
            <Typography {...styles.attributeTypeText}>{`${label}:`}</Typography>
            <Typography {...styles.attributeValueText}>{getAttributeTypeValue(attribute, attributeTypes)}</Typography>
        </GridItem>
    );
};

const ProductFastenersCard = ({
    product,
    tableId,
    showLength,
    showHeadDiameter,
    showThread,
    showPoint,
    showHead,
    showFinish,
}: ProductFastenersCardProps) => {
    const dispatch = useAppDispatch();
    const productInfo = useGetRelatedProductInfoFromList(tableId, product.id);
    const [isExpanded, setIsExpanded] = useState(false);

    if (!productInfo) {
        return null;
    }

    const updateQuantity = (qtyOrdered: number) => {
        dispatch(updateProductInfo({ id: tableId, productId: product.id, qtyOrdered }));
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        dispatch(updateProductInfo({ id: tableId, productId: product.id, unitOfMeasure }));
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.productInfoWrapper}>
                    {productInfo?.productDetailPath ? (
                        <Link
                            {...styles.itemNumberLink}
                            href={productInfo.productDetailPath}
                            data-test-selector="productNumberLink"
                        >
                            {product.productNumber}
                        </Link>
                    ) : (
                        <Typography {...styles.itemNumber}>{product.productNumber}</Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.actionsWrapper}>
                    <ProductPrice showLabel={false} product={productContext} extendedStyles={styles.productPrice} />
                    <AddToCartOptionsAndButton
                        extendedStyles={styles.addToCartOptionsAndButton}
                        showUnitOfMeasure={false}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.viewDetailsWrapper}>
                    {!isExpanded && (
                        <Clickable onClick={() => setIsExpanded(true)} {...styles.detailsClickable}>
                            {translate("View Details")}
                        </Clickable>
                    )}
                </StyledWrapper>
                {product.canAddToWishlist && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.addToListLinkStyles}
                        />
                    </StyledWrapper>
                )}
                {isExpanded && (
                    <StyledWrapper {...styles.attributesWrapper}>
                        <GridContainer {...styles.gridContainer}>
                            {showLength && (
                                <AttributeGridItem
                                    label={translate("Length")}
                                    attribute={constants.productAttributes.length}
                                    attributeTypes={product.attributeTypes}
                                />
                            )}
                            {showHeadDiameter && (
                                <AttributeGridItem
                                    label={translate("Diameter")}
                                    attribute={constants.productAttributes.headDiameter}
                                    attributeTypes={product.attributeTypes}
                                />
                            )}
                            {showThread && (
                                <AttributeGridItem
                                    label={translate("Thread")}
                                    attribute={constants.productAttributes.thread}
                                    attributeTypes={product.attributeTypes}
                                />
                            )}
                            {showPoint && (
                                <AttributeGridItem
                                    label={translate("Point")}
                                    attribute={constants.productAttributes.point}
                                    attributeTypes={product.attributeTypes}
                                />
                            )}
                            {showHead && (
                                <AttributeGridItem
                                    label={translate("Head")}
                                    attribute={constants.productAttributes.head}
                                    attributeTypes={product.attributeTypes}
                                />
                            )}
                            {showFinish && (
                                <AttributeGridItem
                                    label={translate("Finish")}
                                    attribute={constants.productAttributes.finish}
                                    attributeTypes={product.attributeTypes}
                                />
                            )}
                        </GridContainer>
                        <Clickable onClick={() => setIsExpanded(false)} {...styles.detailsClickable}>
                            {translate("Close Details")}
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default ProductFastenersCard;
